import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { getAirports, Airport, AirportApiError } from '../../services/airportApi';
import {
  excelImport,
  ExcelImportError,
  ProjectApiError,
} from '../../services/projectApi';
import { useAuth } from '../../contexts/AuthContext';
import { useModalOpenRegistration } from '../../contexts/ModalOpenContext';
import { getDefaultProjectStartDateString } from '../../utils/dateUtils';

export type ExcelImportMode = 'create' | 'replace';

interface ExcelImportWizardProps {
  open: boolean;
  onClose: () => void;
  mode: ExcelImportMode;
  /** For replace: project id, optional display name, and start date (YYYY-MM-DD) */
  projectId?: number;
  projectName?: string;
  projectStartDate?: string;
  onSuccess: (projectId: number) => void;
}

const steps = ['Project details & file', 'Validating', 'Result'];

function issueActionLabel(issue: ExcelImportError): string {
  if (issue.actionPlanId && issue.actionName) {
    return `${issue.actionPlanId} — ${issue.actionName}`;
  }
  return issue.actionPlanId || issue.actionName || '';
}

function IssueTable({ issues }: { issues: ExcelImportError[] }) {
  const hasRich = issues.some((i) => i.rule || i.why || i.recommendation || i.actionPlanId);
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {hasRich && <TableCell>Severity</TableCell>}
            {hasRich && <TableCell>Action</TableCell>}
            {hasRich && <TableCell>Type</TableCell>}
            {hasRich && <TableCell>Rule</TableCell>}
            <TableCell>Row</TableCell>
            <TableCell>Column</TableCell>
            <TableCell>Problem</TableCell>
            {hasRich && <TableCell>Why</TableCell>}
            {hasRich && <TableCell>How to fix</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {issues.map((err, idx) => (
            <TableRow key={idx}>
              {hasRich && <TableCell>{err.severity || 'error'}</TableCell>}
              {hasRich && <TableCell>{issueActionLabel(err)}</TableCell>}
              {hasRich && <TableCell>{err.actionType || ''}</TableCell>}
              {hasRich && <TableCell>{err.rule || err.code || ''}</TableCell>}
              <TableCell>{err.row}</TableCell>
              <TableCell>{err.column}</TableCell>
              <TableCell>{err.message}</TableCell>
              {hasRich && <TableCell>{err.why || ''}</TableCell>}
              {hasRich && <TableCell>{err.recommendation || ''}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const ExcelImportWizard: React.FC<ExcelImportWizardProps> = ({
  open,
  onClose,
  mode,
  projectId: replaceProjectId,
  projectName: replaceProjectName,
  projectStartDate: replaceProjectStartDate,
  onSuccess,
}) => {
  useModalOpenRegistration(open);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [projectName, setProjectName] = useState('');
  const [projectStartDate, setProjectStartDate] = useState<string>(() => getDefaultProjectStartDateString());
  const [selectedAirportId, setSelectedAirportId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loadingAirports, setLoadingAirports] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ valid: boolean; projectId?: number; errors?: ExcelImportError[]; warnings?: ExcelImportError[] } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isReplace = mode === 'replace' && replaceProjectId != null && replaceProjectId > 0;

  const loadAirports = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingAirports(true);
    setError(null);
    try {
      const response = await getAirports();
      setAirports(response.airports);
    } catch (err) {
      const apiError = err as AirportApiError;
      setError(apiError.message || 'Failed to load airports');
    } finally {
      setLoadingAirports(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (open && !authLoading && isAuthenticated) {
      setProjectStartDate(getDefaultProjectStartDateString());
      loadAirports();
    }
  }, [open, authLoading, isAuthenticated, loadAirports]);

  useEffect(() => {
    if (open && isReplace) {
      if (replaceProjectName) setProjectName(replaceProjectName);
      if (replaceProjectStartDate) setProjectStartDate(replaceProjectStartDate);
    }
  }, [open, isReplace, replaceProjectName, replaceProjectStartDate]);

  const handleClose = () => {
    if (!isSubmitting) {
      setActiveStep(0);
      setProjectName('');
      setProjectStartDate(getDefaultProjectStartDateString());
      setSelectedAirportId('');
      setFile(null);
      setReplaceConfirmed(false);
      setError(null);
      setProgressMessage(null);
      setImportResult(null);
      onClose();
    }
  };

  const canProceedStep1 = () => {
    if (isReplace) {
      return replaceConfirmed && file != null && file.name.toLowerCase().endsWith('.xlsx');
    }
    return (
      projectName.trim() !== '' &&
      projectStartDate !== '' &&
      selectedAirportId !== '' &&
      file != null &&
      file.name.toLowerCase().endsWith('.xlsx')
    );
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!canProceedStep1()) return;
      setError(null);
      setProgressMessage(null);
      setImportResult(null);
      setActiveStep(1);
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('file', file!);
      if (!isReplace) {
        formData.append('projectName', projectName.trim());
        formData.append('projectStartDate', projectStartDate);
        if (selectedAirportId !== '') {
          formData.append('airportId', String(selectedAirportId));
        }
      } else {
        formData.append('projectId', String(replaceProjectId));
        formData.append('projectName', projectName.trim() || replaceProjectName || '');
        formData.append('projectStartDate', replaceProjectStartDate || projectStartDate);
        if (selectedAirportId !== '') {
          formData.append('airportId', String(selectedAirportId));
        }
      }

      try {
        setProgressMessage('Uploading and validating…');
        const result = await excelImport(formData, (msg) => setProgressMessage(msg));
        setImportResult({
          valid: result.valid,
          projectId: result.projectId,
          errors: result.errors,
          warnings: result.warnings,
        });
        setActiveStep(2);
        if (result.valid && result.projectId) {
          onSuccess(result.projectId);
        }
      } catch (err) {
        const apiError = err as ProjectApiError;
        const msg = (err as Error).message || '';
        const isNetworkError = apiError.code === 'NETWORK_ERROR' || (typeof msg === 'string' && (msg.includes('fetch') || msg.includes('network') || msg.includes('Connection') || msg.includes('Failed to fetch')));
        const friendlyMessage = isNetworkError
          ? 'Cannot reach server. Please check your connection. If the project was created, check your project list or Planning page.'
          : (apiError.message || 'Import failed');
        const errorCode = isNetworkError ? 'NETWORK_ERROR' : apiError.code;
        setError(apiError.message || 'Import failed');
        setImportResult({ valid: false, errors: [{ row: 0, column: 'Request', message: friendlyMessage, code: errorCode }] });
        setActiveStep(2);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleFinish = () => {
    const id = importResult?.projectId;
    if (id != null) {
      onSuccess(id);
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isReplace ? 'Replace plan from Excel' : 'Create project from Excel'}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ pt: 1, pb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isReplace && (
              <Alert severity="warning">
                This will replace the current plan for this project. All existing phases, tasks, and actions will be removed and replaced with the contents of the uploaded file.
              </Alert>
            )}

            {!isReplace && (
              <>
                <TextField
                  label="Project Name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                  fullWidth
                  disabled={isSubmitting}
                />
                <TextField
                  label="Project Start Date"
                  type="date"
                  value={projectStartDate}
                  onChange={(e) => setProjectStartDate(e.target.value)}
                  required
                  fullWidth
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth required disabled={isSubmitting || loadingAirports}>
                  <InputLabel id="excel-wizard-airport-label">Associated Airport</InputLabel>
                  <Select
                    labelId="excel-wizard-airport-label"
                    value={selectedAirportId}
                    label="Associated Airport"
                    onChange={(e) => setSelectedAirportId(e.target.value as number | '')}
                  >
                    <MenuItem value="">
                      <em>Select an airport</em>
                    </MenuItem>
                    {airports.map((a) => (
                      <MenuItem key={a.airportId} value={a.airportId}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {isReplace && (
              <FormControl fullWidth>
                <TextField
                  label="Project"
                  value={replaceProjectName || `Project #${replaceProjectId}`}
                  disabled
                  size="small"
                />
              </FormControl>
            )}

            {isReplace && (
              <FormControl required>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setReplaceConfirmed(true)}
                  disabled={replaceConfirmed}
                >
                  {replaceConfirmed ? 'Replace confirmed' : 'I understand, replace the current plan'}
                </Button>
                <FormHelperText>You must confirm before uploading.</FormHelperText>
              </FormControl>
            )}

            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                disabled={isSubmitting}
                fullWidth
              >
                {file ? file.name : 'Choose .xlsx file'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              <FormHelperText>Only .xlsx files are accepted. Max 5 MB.</FormHelperText>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ py: 2 }}>
            {progressMessage && (
              <Typography color="text.secondary" gutterBottom>
                {progressMessage}
              </Typography>
            )}
            <LinearProgress sx={{ mt: 1 }} />
            {isSubmitting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Validating and importing…</Typography>
              </Box>
            )}
          </Box>
        )}

        {activeStep === 2 && importResult && (
          <Box sx={{ py: 1 }}>
            {importResult.valid ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {importResult.projectId != null
                    ? 'Project created successfully. You can open it in Planning.'
                    : 'Import completed successfully.'}
                </Alert>
                {importResult.warnings && importResult.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Import completed with warnings:</Typography>
                    <IssueTable issues={importResult.warnings} />
                  </Alert>
                )}
                <DialogActions>
                  <Button onClick={handleClose}>Close</Button>
                  {importResult.projectId != null && (
                    <Button variant="contained" onClick={handleFinish}>
                      Open in Planning
                    </Button>
                  )}
                </DialogActions>
              </>
            ) : (
              <>
                {(() => {
                  const code = importResult.errors?.[0]?.code;
                  const isConnectionIssue = importResult.errors?.length === 1 &&
                    (code === 'INCOMPLETE_RESPONSE' || code === 'NETWORK_ERROR');
                  return (
                    <Typography color="error" gutterBottom>
                      {isConnectionIssue
                        ? 'Import could not be completed. The connection was interrupted or the server could not be reached.'
                        : 'Validation failed. Please fix the errors in your file and try again.'}
                    </Typography>
                  );
                })()}
                <IssueTable issues={importResult.errors || []} />
                <DialogActions sx={{ mt: 2 }}>
                  <Button onClick={() => { setActiveStep(0); setImportResult(null); setError(null); }}>
                    Try again
                  </Button>
                  <Button onClick={handleClose}>Close</Button>
                </DialogActions>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      {activeStep < 2 && (
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {activeStep === 0 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceedStep1() || isSubmitting}
            >
              Upload and validate
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ExcelImportWizard;
